import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Briefcase,
  Plus,
  Trash2,
  Upload,
  FileText,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Pencil,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { cn, toTitleCase } from "@/lib/utils";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

const OTHER_CATEGORY_VALUE = "otros";

const experienceCategoryOptions = [
  { value: "lider social", label: "Líder social" },
  { value: "discapacidad", label: "Discapacidad" },
  { value: "ddhh", label: "DDHH" },
  { value: "genero y poblacion diversa", label: "Género y población diversa" },
  { value: "emprendimiento", label: "Emprendimiento" },
  { value: "ambiental", label: "Ambiental" },
  { value: "etnico", label: "Étnico" },
  { value: "justicia transicional victimas tierra", label: "Justicia transicional víctimas tierra" },
  { value: "administracion", label: "Administración" },
  { value: "infraestructura-vivienda", label: "Infraestructura-vivienda" },
  { value: "paz", label: "Paz" },
  { value: "grupos etareos", label: "Grupos etáreos" },
  { value: "investigacion", label: "Investigación" },
  { value: OTHER_CATEGORY_VALUE, label: "Otra" },
];

const formatExperienceCategory = (value) => {
  const match = experienceCategoryOptions.find((option) => option.value === value);
  return match ? match.label : toTitleCase(value);
};

const knownCategoryValues = new Set(
  experienceCategoryOptions
    .map((option) => option.value)
    .filter((value) => value !== OTHER_CATEGORY_VALUE)
);

const parseOtherCategoriesInput = (value = "") =>
  value
    .split(/[,\n;]+/g)
    .map((item) => item.trim())
    .filter(Boolean);

const mergeCategoryValues = (currentValues = [], incomingValues = []) => {
  const seen = new Set(currentValues.map((item) => item.trim().toLowerCase()).filter(Boolean));
  const merged = [...currentValues];
  incomingValues.forEach((value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized || normalized === OTHER_CATEGORY_VALUE || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    merged.push(normalized);
  });
  return merged;
};

const formSchema = z.object({
  company: z.string().min(2, "El Nombre De La Empresa Es Requerido"),
  position: z.string().min(2, "El Cargo Es Requerido"),
  start_date: z.string().min(1, "La Fecha De Inicio Es Requerida"),
  end_date: z.string().optional(),
  current: z.boolean().default(false),
  experience_categories: z.array(z.string()).min(1, "Seleccione Al Menos Una Categoría"),
  other_category: z.string().optional(),
}).superRefine((data, ctx) => {
  if (
    data.experience_categories?.includes(OTHER_CATEGORY_VALUE) &&
    parseOtherCategoriesInput(data.other_category || "").length === 0
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Especifique Una O Varias Categorías En Otra",
      path: ["other_category"],
    });
  }
});

export default function WorkExperienceForm({ profile, profileId, setProfile, onNext, onPrev, loading }) {
  const [entries, setEntries] = useState(profile?.work_experience || []);
  const [dynamicCategoryValues, setDynamicCategoryValues] = useState([]);
  const [categorySelectorOpen, setCategorySelectorOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [certUrl, setCertUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isCurrent, setIsCurrent] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [activeStudyId, setActiveStudyId] = useState(null);
  const [showUnlinkedSection, setShowUnlinkedSection] = useState(false);
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company: "",
      position: "",
      start_date: "",
      end_date: "",
      current: false,
      experience_categories: [],
      other_category: "",
    },
  });

  const selectedCategories = watch("experience_categories") || [];
  const selectableCategoryValues = new Set([...knownCategoryValues, ...dynamicCategoryValues]);
  const dynamicCategoryOptions = dynamicCategoryValues.map((value) => ({
    value,
    label: toTitleCase(value),
  }));
  const categoryOptions = [
    ...experienceCategoryOptions.filter((option) => option.value !== OTHER_CATEGORY_VALUE),
    ...dynamicCategoryOptions.filter((option) => !knownCategoryValues.has(option.value)),
    { value: OTHER_CATEGORY_VALUE, label: "Otra" },
  ];
  const selectedCategoryLabels = selectedCategories
    .map((value) => formatExperienceCategory(value))
    .join(", ");

  useEffect(() => {
    let isMounted = true;

    const loadDynamicCategories = async () => {
      try {
        const response = await axios.get(`${API}/work/categories`);
        if (!isMounted) return;
        const categories = Array.isArray(response.data?.categories)
          ? response.data.categories
          : [];
        setDynamicCategoryValues((prev) => mergeCategoryValues(prev, categories));
      } catch (error) {
        console.error("Error loading work category options:", error);
      }
    };

    loadDynamicCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleCertUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("El Archivo No Debe Superar Los 10MB");
      return;
    }

    setUploadingCert(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCertUrl(`${BACKEND_URL}${response.data.url}`);
      toast.success("Certificado Subido Correctamente");
    } catch (error) {
      console.error("Error uploading certificate:", error);
      toast.error("Error Al Subir El Certificado");
    } finally {
      setUploadingCert(false);
    }
  };

  const handleCurrentChange = (checked) => {
    setIsCurrent(checked);
    setValue("current", checked);
    if (checked) {
      setValue("end_date", "");
    }
  };

  const splitCategories = (categories = []) => {
    const selection = [];
    const others = [];
    categories.forEach((value) => {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return;
      if (selectableCategoryValues.has(normalized)) {
        selection.push(normalized);
      } else {
        others.push(value);
      }
    });
    if (others.length) {
      selection.push(OTHER_CATEGORY_VALUE);
    }
    return { selection, other: others.join(", ") };
  };

  const handleCategorySelect = (value) => {
    const current = new Set(getValues("experience_categories") || []);
    if (current.has(value)) {
      current.delete(value);
    } else {
      current.add(value);
    }

    const updated = Array.from(current);
    setValue("experience_categories", updated, { shouldValidate: true });
    if (!updated.includes(OTHER_CATEGORY_VALUE)) {
      setValue("other_category", "", { shouldValidate: true });
      clearErrors("other_category");
    }
    if (updated.length > 0) {
      clearErrors("experience_categories");
    }
  };

  const handleOtherCategoryBlur = (event) => {
    const value = event.target.value.trim();
    event.target.value = value;
    setValue("other_category", value, { shouldValidate: true });
  };

  const onSubmitEntry = async (data) => {
    if (!profileId) {
      toast.error("Primero Complete Los Datos Personales");
      return;
    }

    if (!certUrl && !data.current) {
      toast.error("Debe Subir El Certificado Laboral");
      return;
    }

    const selected = data.experience_categories || [];
    const otherCategories = parseOtherCategoriesInput(data.other_category || "");
    const finalCategories = [];
    const seenCategories = new Set();

    selected
      .filter((item) => item !== OTHER_CATEGORY_VALUE)
      .forEach((item) => {
        const normalized = String(item || "").trim().toLowerCase();
        if (!normalized || seenCategories.has(normalized)) return;
        seenCategories.add(normalized);
        finalCategories.push(normalized);
      });

    if (selected.includes(OTHER_CATEGORY_VALUE)) {
      otherCategories.forEach((item) => {
        const normalized = String(item || "").trim().toLowerCase();
        if (!normalized || seenCategories.has(normalized)) return;
        seenCategories.add(normalized);
        finalCategories.push(normalized);
      });
    }

    setSubmitting(true);
    try {
      const { other_category, experience_categories, ...rest } = data;
      const entry = {
        ...rest,
        experience_categories: finalCategories,
        certificate_url: certUrl,
      };
      if (activeStudyId) {
        entry.academic_id = activeStudyId;
      }

      const response = editingEntryId
        ? await axios.put(`${API}/profiles/${profileId}/work/${editingEntryId}`, entry)
        : await axios.post(`${API}/profiles/${profileId}/work`, entry);
      setEntries(response.data.work_experience);
      setProfile(response.data);
      setDynamicCategoryValues((prev) => mergeCategoryValues(prev, finalCategories));
      setIsAdding(false);
      reset();
      setCertUrl(null);
      setIsCurrent(false);
      setActiveStudyId(null);
      setEditingEntryId(null);
      toast.success(editingEntryId ? "Experiencia Actualizada" : "Experiencia Agregada Correctamente");
    } catch (error) {
      console.error("Error adding work entry:", error);
      toast.error(editingEntryId ? "Error Al Actualizar La Experiencia" : "Error Al Agregar La Experiencia");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      const response = await axios.delete(`${API}/profiles/${profileId}/work/${entryId}`);
      setEntries(response.data.work_experience);
      setProfile(response.data);
      toast.success("Experiencia Eliminada");
    } catch (error) {
      console.error("Error deleting work entry:", error);
      toast.error("Error Al Eliminar La Experiencia");
    }
  };

  const cancelAdd = () => {
    setIsAdding(false);
    reset();
    setCertUrl(null);
    setIsCurrent(false);
    setActiveStudyId(null);
    setEditingEntryId(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Presente";
    const [year, month] = dateStr.split("-");
    const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const missingCertificates = entries.some((entry) => !entry.current && !entry.certificate_url);

  const handleNext = () => {
    if (entries.length === 0) {
      toast.error("Agregue Al Menos Una Experiencia");
      return;
    }
    if (missingCertificates) {
      toast.error("Debe Subir Certificado Laboral En Experiencias Que No Sean Trabajo Actual");
      return;
    }
    onNext();
  };

  const startAdd = (studyId = null) => {
    setIsAdding(true);
    setActiveStudyId(studyId);
    if (studyId === null) {
      setShowUnlinkedSection(true);
    }
    reset();
    setCertUrl(null);
    setIsCurrent(false);
    setEditingEntryId(null);
  };

  const startEdit = (entry) => {
    const { selection, other } = splitCategories(entry.experience_categories || []);
    setIsAdding(true);
    setEditingEntryId(entry.id);
    setActiveStudyId(entry.academic_id || null);
    if (!entry.academic_id) {
      setShowUnlinkedSection(true);
    }
    setCertUrl(entry.certificate_url || null);
    setIsCurrent(Boolean(entry.current));
    reset({
      company: entry.company || "",
      position: entry.position || "",
      start_date: entry.start_date || "",
      end_date: entry.end_date || "",
      current: Boolean(entry.current),
      experience_categories: selection,
      other_category: other,
    });
  };

  const getAcademicLevelLabel = (value) => {
    const levels = {
      tecnica: "Técnica",
      tecnologica: "Tecnológica",
      universitaria: "Universitaria",
      especializacion: "Especialización",
      maestria: "Maestría",
      doctorado: "Doctorado",
      curso: "Curso / Certificación",
    };
    return levels[value] || value;
  };

  const renderWorkEntry = (entry) => {
    const categories = entry.experience_categories || [];
    const categoryLabels = categories.map(formatExperienceCategory);

    return (
      <Card key={entry.id} className="entry-card border border-gray-200 rounded-sm">
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-gray-500">
                  {formatDate(entry.start_date)} - {entry.current ? "Presente" : formatDate(entry.end_date)}
                </span>
                {entry.current && (
                  <span className="text-xs font-semibold tracking-wide text-green-600 bg-green-50 px-2 py-1 rounded">
                    Actual
                  </span>
                )}
              </div>
              <h4 className="font-semibold text-gray-900">{entry.position}</h4>
              <p className="text-sm text-yellow-600 font-medium">{entry.company}</p>
              {categoryLabels.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {categoryLabels.map((label) => (
                    <span
                      key={`${entry.id}-${label}`}
                      className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              ) : entry.description ? (
                <p className="text-sm text-gray-600 mt-2">{entry.description}</p>
              ) : null}
              {entry.certificate_url && (
                <a
                  href={entry.certificate_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-blue-600 hover:underline mt-2"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Ver Certificado
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-yellow-600"
                onClick={() => startEdit(entry)}
                data-testid={`edit-work-${entry.id}`}
              >
                <Pencil className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-red-500"
                onClick={() => handleDeleteEntry(entry.id)}
                data-testid={`delete-work-${entry.id}`}
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const academicEntries = profile?.academic_info || [];
  const knownStudyIds = new Set(academicEntries.map((study) => study.id));
  const unlinkedEntries = entries.filter(
    (entry) => !entry.academic_id || !knownStudyIds.has(entry.academic_id)
  );
  const canOpenUnlinkedSection = !(isAdding && activeStudyId);
  const unlinkedSectionVisible =
    showUnlinkedSection || unlinkedEntries.length > 0 || (isAdding && !activeStudyId);
  const activeStudy = academicEntries.find((study) => study.id === activeStudyId);

  const sortWorkEntries = (items) => {
    const toDate = (value) => {
      if (!value) return null;
      const [year, month] = value.split("-");
      if (!year || !month) return null;
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    };
    return [...items].sort((a, b) => {
      if (a.current && !b.current) return -1;
      if (!a.current && b.current) return 1;
      const dateA = toDate(a.end_date || a.start_date);
      const dateB = toDate(b.end_date || b.start_date);
      const timeA = dateA ? dateA.getTime() : 0;
      const timeB = dateB ? dateB.getTime() : 0;
      return timeB - timeA;
    });
  };

  const sortedAcademicEntries = [...academicEntries].sort((a, b) => {
    const yearA = parseInt(a.year, 10) || 0;
    const yearB = parseInt(b.year, 10) || 0;
    return yearB - yearA;
  });

  const renderAddForm = () => (
    <Card className="border-2 border-yellow-400 rounded-sm mb-6">
      <CardContent className="p-6">
        <h3 className="font-oswald text-lg font-semibold mb-4">
          {editingEntryId ? "Editar Experiencia" : "Nueva Experiencia"}
        </h3>
        {activeStudy && (
          <div className="mb-4 rounded-sm border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
            Asociada A: {activeStudy.title}
            {activeStudy.year ? ` (${activeStudy.year})` : ""}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmitEntry)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-field">
              <Label htmlFor="company" className="text-sm font-medium text-gray-700 tracking-wide">
                Empresa
              </Label>
              <Input
                id="company"
                {...register("company", {
                  onBlur: (event) => {
                    const value = toTitleCase(event.target.value);
                    event.target.value = value;
                    setValue("company", value, { shouldValidate: true });
                  },
                })}
                placeholder="Nombre De La Empresa"
                className="mt-1 h-12 rounded-sm"
                data-testid="work-company-input"
              />
              {errors.company && (
                <p className="text-red-500 text-sm mt-1">{errors.company.message}</p>
              )}
            </div>

            <div className="form-field">
              <Label htmlFor="position" className="text-sm font-medium text-gray-700 tracking-wide">
                Cargo
              </Label>
              <Input
                id="position"
                {...register("position", {
                  onBlur: (event) => {
                    const value = toTitleCase(event.target.value);
                    event.target.value = value;
                    setValue("position", value, { shouldValidate: true });
                  },
                })}
                placeholder="Cargo Desempeñado"
                className="mt-1 h-12 rounded-sm"
                data-testid="work-position-input"
              />
              {errors.position && (
                <p className="text-red-500 text-sm mt-1">{errors.position.message}</p>
              )}
            </div>

            <div className="form-field">
              <Label htmlFor="start_date" className="text-sm font-medium text-gray-700 tracking-wide">
                Fecha De Inicio
              </Label>
              <Input
                id="start_date"
                type="month"
                {...register("start_date")}
                className="mt-1 h-12 rounded-sm"
                data-testid="work-start-date-input"
              />
              {errors.start_date && (
                <p className="text-red-500 text-sm mt-1">{errors.start_date.message}</p>
              )}
            </div>

            <div className="form-field">
              <Label htmlFor="end_date" className="text-sm font-medium text-gray-700 tracking-wide">
                Fecha De Fin
              </Label>
              <Input
                id="end_date"
                type="month"
                {...register("end_date")}
                disabled={isCurrent}
                className="mt-1 h-12 rounded-sm disabled:opacity-50"
                data-testid="work-end-date-input"
              />
              <div className="flex items-center gap-2 mt-2">
                <Checkbox
                  id="current"
                  checked={isCurrent}
                  onCheckedChange={handleCurrentChange}
                  data-testid="work-current-checkbox"
                />
                <Label htmlFor="current" className="text-sm text-gray-600 cursor-pointer">
                  Trabajo Actual
                </Label>
              </div>
            </div>

            <div className="form-field md:col-span-2">
              <Label className="text-sm font-medium text-gray-700 tracking-wide">
                Categorías De Experiencia
              </Label>
              <p className="text-xs text-gray-500 mt-1">
                Seleccione Varias Categorías. Puede Buscar Por Coincidencia.
              </p>
              <Popover open={categorySelectorOpen} onOpenChange={setCategorySelectorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-3 h-12 w-full justify-between rounded-sm border-gray-300 text-left font-normal"
                    data-testid="work-category-multiselect-trigger"
                  >
                    <span className="truncate">
                      {selectedCategoryLabels || "Seleccione Categorías"}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar Categoría..." />
                    <CommandList>
                      <CommandEmpty>No Hay Coincidencias.</CommandEmpty>
                      <CommandGroup>
                        {categoryOptions.map((option) => {
                          const isSelected = selectedCategories.includes(option.value);
                          return (
                            <CommandItem
                              key={option.value}
                              value={`${option.value} ${option.label}`}
                              onSelect={() => handleCategorySelect(option.value)}
                              data-testid={`work-category-${option.value.replace(/\s+/g, "-")}`}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  isSelected ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {option.label}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                  <div className="border-t border-gray-200 p-2">
                    <Button
                      type="button"
                      className="w-full bg-black text-yellow-400 hover:bg-gray-900 rounded-sm"
                      onClick={() => setCategorySelectorOpen(false)}
                      data-testid="work-category-confirm-button"
                    >
                      Confirmar
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              <input type="hidden" {...register("experience_categories")} />
              {selectedCategories.includes(OTHER_CATEGORY_VALUE) && (
                <div className="mt-3">
                  <Textarea
                    {...register("other_category", {
                      onChange: () => clearErrors("other_category"),
                      onBlur: handleOtherCategoryBlur,
                    })}
                    placeholder="Escriba Una O Varias Categorías Separadas Por Coma"
                    className="min-h-[90px] rounded-sm"
                    data-testid="work-category-other-input"
                  />
                </div>
              )}
              {errors.experience_categories && (
                <p className="text-red-500 text-sm mt-2">{errors.experience_categories.message}</p>
              )}
              {errors.other_category && (
                <p className="text-red-500 text-sm mt-1">{errors.other_category.message}</p>
              )}
            </div>
          </div>

          {/* Certificate Upload */}
          <div className="form-field">
            <Label className="text-sm font-medium text-gray-700 tracking-wide">
              Certificado Laboral
            </Label>
            <p className="text-xs text-gray-500 mt-1">
              {isCurrent ? "Opcional Para Trabajo Actual." : "Obligatorio."}
            </p>
            <div
              className="file-upload-zone mt-1 p-6 rounded-sm text-center cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="work-certificate-upload-zone"
            >
              {certUrl ? (
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <FileText className="w-6 h-6" />
                  <span>Certificado Cargado</span>
                </div>
              ) : uploadingCert ? (
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">
                    Haga Clic Para Subir El Certificado
                  </p>
                  <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG (Máx. 10MB)</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleCertUpload}
              className="hidden"
              data-testid="work-certificate-input"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={cancelAdd}
              className="flex-1 h-12 rounded-sm tracking-wide"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-black text-yellow-400 hover:bg-gray-900 h-12 rounded-sm tracking-wide font-bold"
              data-testid="add-work-submit"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : editingEntryId ? "Guardar Cambios" : "Agregar"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="work-experience-form">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-yellow-400 rounded flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-black" />
          </div>
          <h2 className="font-oswald text-2xl lg:text-3xl font-bold text-black tracking-wide">
            Experiencia Laboral
          </h2>
        </div>
        <p className="text-gray-500 ml-13">Agregue Su Historial Profesional</p>
      </div>

      {/* Studies and Experiences */}
      {sortedAcademicEntries.length > 0 ? (
        <div className="space-y-6 mb-6">
          {sortedAcademicEntries.map((study) => {
            const studyEntries = sortWorkEntries(
              entries.filter((entry) => entry.academic_id === study.id)
            );
            const isStudyActive = isAdding && activeStudyId === study.id;

            return (
              <Card key={study.id} className="border border-gray-200 rounded-sm">
                <CardContent className="p-5">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-semibold tracking-wide text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                            {getAcademicLevelLabel(study.level)}
                          </span>
                          {study.year && (
                            <span className="text-xs text-gray-500">{study.year}</span>
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900 mt-2">{study.title}</h4>
                        {study.institution && (
                          <p className="text-sm text-gray-600">{study.institution}</p>
                        )}
                        {study.branch && (
                          <p className="text-xs text-gray-500 mt-1">Rama: {study.branch}</p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => startAdd(study.id)}
                        disabled={isAdding && activeStudyId !== study.id}
                        className="h-10 px-4 rounded-sm tracking-wide bg-yellow-400 text-black border-yellow-400 hover:bg-yellow-500 hover:text-black font-semibold shadow-sm"
                        data-testid={`add-work-for-study-${study.id}`}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {studyEntries.length > 0 ? "Agregar Otra Experiencia" : "Agregar Experiencia"}
                      </Button>
                    </div>

                    {studyEntries.length > 0 ? (
                      <div className="space-y-3">
                        {studyEntries.map((entry) => renderWorkEntry(entry))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">
                        No Hay Experiencia Asociada A Este Estudio.
                      </p>
                    )}

                    {isStudyActive && renderAddForm()}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border border-gray-200 rounded-sm mb-6">
          <CardContent className="p-4 text-sm text-gray-500">
            No Hay Estudios Registrados Todavía.
          </CardContent>
        </Card>
      )}

      {/* Unlinked Experiences */}
      <div className="mb-6">
        <Card className="border border-gray-200 rounded-sm">
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h3 className="font-oswald text-lg font-semibold">
                  Experiencia Sin Estudio Asociado
                </h3>
                <p className="text-sm text-gray-500">
                  Agregue Experiencia Laboral Que No Tenga Soporte De Estudio Registrado
                </p>
              </div>
              <Button
                type="button"
                onClick={() => startAdd(null)}
                disabled={!canOpenUnlinkedSection}
                className="h-10 px-4 rounded-sm tracking-wide bg-black text-yellow-400 hover:bg-gray-900 hover:text-yellow-300 disabled:opacity-50"
                data-testid="open-unlinked-work-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Experiencia Laboral Sin Soporte De Estudio
              </Button>
            </div>

            {unlinkedSectionVisible && (
              <div className="mt-5 space-y-4">
                {unlinkedEntries.length > 0 ? (
                  <div className="space-y-3">
                    {sortWorkEntries(unlinkedEntries).map((entry) => renderWorkEntry(entry))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No Hay Experiencia Registrada.</p>
                )}

                {isAdding && !activeStudyId && renderAddForm()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onPrev}
          className="h-12 px-6 rounded-sm tracking-wide"
          data-testid="work-prev-button"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Anterior
        </Button>
        <Button
          type="button"
          onClick={handleNext}
          disabled={loading}
          className="bg-black text-yellow-400 hover:bg-gray-900 hover:text-yellow-300 tracking-wide font-bold px-8 h-12 rounded-sm btn-primary"
          data-testid="work-next-button"
        >
          Siguiente
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
}
